import React, { useEffect, useRef, useState } from "react";
import { Box, Stepper, Step, StepLabel } from "@mui/material";
import { LoaderComponent } from '../loaders/page-loader';

interface StepData {
  label: string;
  content: React.ReactNode;
}

interface CustomStepperProps {
  steps: StepData[];
  activeStep: number;
  onStepChange: (step: number) => void;
  isNonLinear?: boolean;
}

const CustomStepper: React.FC<CustomStepperProps> = ({
  steps,
  activeStep,
  onStepChange,
  isNonLinear = false,
}) => {
  const handleStepClick = (stepIndex: number) => {
    if (isNonLinear) {
      onStepChange(stepIndex);
    }
  };

  // Show LoaderComponent in the content area for 250 ms whenever the active
  // step changes. The stepper header itself remains visible throughout.
  const [isTransitioning, setIsTransitioning] = useState(false);
  const prevStep = useRef(activeStep);

  useEffect(() => {
    if (prevStep.current !== activeStep) {
      prevStep.current = activeStep;
      setIsTransitioning(true);
      const timer = setTimeout(() => setIsTransitioning(false), 250);
      return () => clearTimeout(timer);
    }
  }, [activeStep]);

  return (
    <Box>
      <Stepper
        nonLinear={isNonLinear}
        activeStep={activeStep}
        alternativeLabel
        sx={{
          "& .MuiStepLabel-root .Mui-active": {
            color: "#353c52", // Change the color of the active step
          },
          "& .MuiStepLabel-root .Mui-completed": {
            color: "#353c52", // Change the color of completed steps
          },
        }}
      >
        {steps.map((step, index) => (
          <Step key={index} onClick={() => handleStepClick(index)}>
            <StepLabel>{step.label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Content area — loader shown during step transitions, stepper header always visible */}
      <Box marginTop={2}>
        {isTransitioning ? (
          <LoaderComponent />
        ) : (
          steps[activeStep] && <Box>{steps[activeStep].content}</Box>
        )}
      </Box>
    </Box>
  );
};

export default CustomStepper;
